import { FormControl } from '@Backlot-Cars/archie';
import { publicEnvConfig } from 'helpers/env';
import { pickByType } from 'helpers/address/parser';
import dynamic from 'next/dynamic';
import { useCallback, useRef } from 'react';
import { PlacePickerContainer } from './styles';

const APILoader = dynamic(() => import('@googlemaps/extended-component-library/react').then((mod) => mod.APILoader), {
	ssr: false,
});
const PlacePicker = dynamic(
	() => import('@googlemaps/extended-component-library/react').then((mod) => mod.PlacePicker),
	{ ssr: false },
);

const createSessionToken = () => {
	/* global google */
	if (typeof google !== 'undefined' && google.maps?.places?.AutocompleteSessionToken) {
		return new google.maps.places.AutocompleteSessionToken();
	}
	return null;
};

const parseAddressComponents = (components) => {
	const result = { street: '', street_line_two: '', city: '', state: '', zipcode: '' };
	if (!Array.isArray(components)) return result;

	const streetNumber = pickByType(components, 'street_number')?.longText || '';
	const route = pickByType(components, 'route')?.longText || '';
	result.street_line_two = pickByType(components, 'subpremise')?.longText || '';
	result.city = pickByType(components, 'locality')?.longText || '';
	result.state = pickByType(components, 'administrative_area_level_1')?.shortText || '';
	const zip = pickByType(components, 'postal_code')?.longText || '';

	result.street = [streetNumber, route].filter(Boolean).join(' ').trim();
	result.zipcode = zip;
	return result;
};

export const AddressAutocomplete = ({
	register,
	errors,
	setValue,
	onAddressSelected,
	fieldNames = {
		address: 'dealershipAddress',
		address2: 'dealershipAddress2',
		city: 'city',
		state: 'state',
		zip: 'zip',
		latitude: 'latitude',
		longitude: 'longitude',
	},
	label,
	placeholder,
	inputName = 'dealershipAddress',
}) => {
	const sessionTokenRef = useRef(createSessionToken());

	const formFields = [
		{ name: fieldNames.address, id: fieldNames.address },
		{ name: fieldNames.address2, id: fieldNames.address2 },
		{ name: fieldNames.city, id: fieldNames.city },
		{ name: fieldNames.state, id: fieldNames.state },
		{ name: fieldNames.zip, id: fieldNames.zip },
		{ name: fieldNames.latitude, id: fieldNames.latitude },
		{ name: fieldNames.longitude, id: fieldNames.longitude },
	];

	const handlePlaceChange = useCallback(
		(e) => {
			const place = e?.target?.value;

			if (!place) {
				const opts = { shouldValidate: true, shouldDirty: true };
				if (setValue) {
					setValue(fieldNames.address, '', opts);
					setValue(fieldNames.address2, '', opts);
					setValue(fieldNames.city, '', opts);
					setValue(fieldNames.state, '', opts);
					setValue(fieldNames.zip, '', opts);
					setValue(fieldNames.latitude, '', opts);
					setValue(fieldNames.longitude, '', opts);
				}
				if (onAddressSelected) {
					onAddressSelected({
						[fieldNames.address]: '',
						[fieldNames.address2]: '',
						[fieldNames.city]: '',
						[fieldNames.state]: '',
						[fieldNames.zip]: '',
						[fieldNames.latitude]: undefined,
						[fieldNames.longitude]: undefined,
					});
				}
				return;
			}

			const address = parseAddressComponents(place.addressComponents);
			const opts = { shouldValidate: true, shouldDirty: true };

			if (setValue) {
				setValue(fieldNames.address, address.street, opts);
				setValue(fieldNames.address2, address.street_line_two, opts);
				setValue(fieldNames.city, address.city, opts);
				setValue(fieldNames.state, address.state, opts);
				setValue(fieldNames.zip, address.zipcode, opts);

				if (
					place.location &&
					typeof place.location.lat === 'number' &&
					typeof place.location.lng === 'number'
				) {
					setValue(fieldNames.latitude, place.location.lat, opts);
					setValue(fieldNames.longitude, place.location.lng, opts);
				}
			}

			sessionTokenRef.current = createSessionToken();

			if (onAddressSelected) {
				onAddressSelected({
					[fieldNames.address]: address.street,
					[fieldNames.address2]: address.street_line_two,
					[fieldNames.city]: address.city,
					[fieldNames.state]: address.state,
					[fieldNames.zip]: address.zipcode,
					[fieldNames.latitude]: place.location?.lat,
					[fieldNames.longitude]: place.location?.lng,
				});
			}
		},
		[setValue, onAddressSelected, fieldNames],
	);

	return (
		<>
			<APILoader
				apiKey={publicEnvConfig.GOOGLE_PLACES_API_KEY}
				solutionChannel="GMP_GCC_placepicker_v1"
				style={{ display: 'none' }}
			/>
			<FormControl label={label} required formErrors={errors} inputName={inputName}>
				<PlacePickerContainer>
					<PlacePicker
						country={['us']}
						placeholder={placeholder}
						onPlaceChange={handlePlaceChange}
						sessionToken={sessionTokenRef.current?.toString()}
					/>
				</PlacePickerContainer>
			</FormControl>

			{formFields.map((field) => (
				<input key={field.id} id={field.id} type="hidden" defaultValue="" {...register(field.name)} />
			))}
		</>
	);
};

export default AddressAutocomplete;
